import React, { createContext, useContext, useState, useMemo, ReactNode } from "react";
import {AuswertungKontaktaufnahme} from "../api";


interface KontaktaufnahmeContextProps {
    kontaktaufnahmen: AuswertungKontaktaufnahme[];
    selectedIndex: number;
    selectedKontaktaufnahme: AuswertungKontaktaufnahme | null;
    setSelectedIndex: (index: number) => void;
    next: () => void;
    prev: () => void;
}

const KontaktaufnahmeContext = createContext<KontaktaufnahmeContextProps | null>(null);

export const useKontaktaufnahme = () => {
    const ctx = useContext(KontaktaufnahmeContext);
    if (!ctx) {
        throw new Error("useKontaktaufnahme must be used inside <KontaktaufnahmeProvider>");
    }
    return ctx;
};

interface ProviderProps {
    kontaktaufnahmen: AuswertungKontaktaufnahme[];
    initialKontaktaufnahmeId?: string;
    children: ReactNode;
}

export const KontaktaufnahmeProvider = ({
                                            kontaktaufnahmen,
                                            initialKontaktaufnahmeId,
                                            children,
                                        }: ProviderProps) => {
    const [selectedId, setSelectedId] = useState<string | undefined>(initialKontaktaufnahmeId);

    const selectedIndex = useMemo(() => {
        const index = kontaktaufnahmen.findIndex((k) => k.id === selectedId);
        return index !== -1 ? index : Math.max(kontaktaufnahmen.length - 1, 0);
    }, [kontaktaufnahmen, selectedId]);

    const selectedKontaktaufnahme = kontaktaufnahmen[selectedIndex] || null;

    const setSelectedIndex = (index: number) => {
        setSelectedId(kontaktaufnahmen[index]?.id);
    };

    const next = () => setSelectedIndex(Math.min(selectedIndex + 1, kontaktaufnahmen.length - 1));

    const prev = () => setSelectedIndex(Math.max(selectedIndex - 1, 0));

    return (
        <KontaktaufnahmeContext.Provider
            value={{
                kontaktaufnahmen,
                selectedIndex,
                selectedKontaktaufnahme,
                setSelectedIndex,
                next,
                prev,
            }}
        >
            {children}
        </KontaktaufnahmeContext.Provider>
    );
};